/**
 * MemberDetailPage — the member-detail screen reached from Account & members
 * (T12.2). Route `/settings/account/members/:userId`. Carries the new member
 * fields, each auto-saving through `PATCH /api/household/users/:id`:
 *   - Age group — segmented Child / Teen / Adult / Senior (sharpens quantity scaling)
 *   - Dietary exclusions — per-member exclusion chips (bias away from matching)
 *
 * Manager-only on the backend; the surface still renders for members but the
 * writes return 403 (the controls disable for non-managers so the affordance is
 * honest).
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useHouseholdUsers,
  useHouseholds,
  useUpdateMemberFields,
} from '@shopista/shared/api/hooks';
import { AGE_GROUPS } from '@shopista/shared/types';
import { apiErrorMessage } from '@shopista/shared/api';
import { Spinner } from '../../components';
import { routes } from '../../routes/paths';
import { SettingsLayover } from './SettingsLayover';
import {
  setgroupStyle,
  h4Style,
  hintStyle,
  tagsRowStyle,
  TagToggle,
  Choice,
  type ScaleOption,
} from './prefControls';

const EXCLUSION_OPTIONS = [
  'Vegetarian',
  'No pork',
  'No dairy',
  'Gluten-free',
  'No shellfish',
] as const;

const AGE_CHOICES: ScaleOption[] = AGE_GROUPS.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

const errorStyle = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
} as const;

export function MemberDetailPage(): JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const usersQuery = useHouseholdUsers();
  const householdsQuery = useHouseholds();
  const updateMember = useUpdateMemberFields();
  const [actionError, setActionError] = useState<string | null>(null);

  const currentMembership = (householdsQuery.data ?? []).find((h) => h.is_current);
  const isManager = currentMembership?.role === 'manager';

  const member = (usersQuery.data?.users ?? []).find((u) => u.id === userId);

  const save = async (input: {
    ageGroup?: string | null;
    exclusions?: string[];
  }) => {
    if (!userId) return;
    setActionError(null);
    try {
      await updateMember.mutateAsync({ userId, input });
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not save member details.'));
    }
  };

  const band = {
    variant: 'steers' as const,
    children: (
      <>
        This member&apos;s <strong>age</strong> feeds quantity scaling. Their{' '}
        <strong>exclusions</strong> downrank matching categories in suggestions.
      </>
    ),
  };

  if (usersQuery.isLoading) {
    return (
      <SettingsLayover
        emoji="👤"
        title="Member"
        backLabel="‹ Members"
        backTo={routes.settingsAccount}
        testId="settings-member-detail"
        band={band}
      >
        <Spinner />
      </SettingsLayover>
    );
  }

  if (!member) {
    return (
      <SettingsLayover
        emoji="👤"
        title="Member"
        backLabel="‹ Members"
        backTo={routes.settingsAccount}
        testId="settings-member-detail"
      >
        <p style={hintStyle} data-testid="settings-member-detail-missing">
          This member is no longer in the household.
        </p>
      </SettingsLayover>
    );
  }

  const label = member.display_name ?? member.email;
  const ageGroup = member.age_group ?? null;
  const exclusions = member.exclusions ?? [];

  const toggleExclusion = (value: string) => {
    const next = exclusions.includes(value)
      ? exclusions.filter((e) => e !== value)
      : [...exclusions, value];
    void save({ exclusions: next });
  };

  return (
    <SettingsLayover
      emoji="👤"
      title={label}
      backLabel="‹ Members"
      backTo={routes.settingsAccount}
      testId="settings-member-detail"
      band={band}
    >
      {actionError && (
        <div role="alert" style={errorStyle} data-testid="settings-member-detail-error">
          {actionError}
        </div>
      )}

      {/* Age group */}
      <section style={setgroupStyle} data-testid="settings-member-age">
        <h4 style={h4Style}>Age</h4>
        <p style={hintStyle}>
          A range is fine — we only use it to scale how much you tend to need.
        </p>
        {isManager ? (
          <Choice
            options={AGE_CHOICES}
            value={ageGroup}
            onPick={(value) => void save({ ageGroup: value })}
            testId="settings-member-age-choice"
          />
        ) : (
          <p style={hintStyle} data-testid="settings-member-age-readonly">
            {ageGroup ? AGE_CHOICES.find((c) => c.value === ageGroup)?.label : 'Not set'}
          </p>
        )}
      </section>

      {/* Dietary exclusions */}
      <section style={setgroupStyle} data-testid="settings-member-exclusions">
        <h4 style={h4Style}>
          Dietary exclusions{' '}
          <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
            · this member
          </span>
        </h4>
        <p style={hintStyle}>
          What this person doesn&apos;t eat. Tap to toggle. These bias suggestions
          away from matching products.
        </p>
        <div style={tagsRowStyle}>
          {EXCLUSION_OPTIONS.map((opt) => (
            <TagToggle
              key={opt}
              label={opt}
              on={exclusions.includes(opt)}
              onToggle={() => isManager && toggleExclusion(opt)}
              testId={`settings-member-exclusion-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            />
          ))}
        </div>
      </section>
    </SettingsLayover>
  );
}
